import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, Search, Edit2, Trash2, Loader2, X, MapPin, Clock, User
} from 'lucide-react';
import { clubsAPI, teachersAPI } from '../services/api';
import Pagination from '../components/Pagination';

const CATEGORIES = {
  sport: { name: 'Sport', icon: '⚽', color: '#22c55e' },
  art: { name: "San'at", icon: '🎨', color: '#f59e0b' },
  science: { name: 'Fan', icon: '🔬', color: '#3b82f6' },
  language: { name: 'Til', icon: '🌍', color: '#8b5cf6' },
  tech: { name: 'Texnologiya', icon: '💻', color: '#06b6d4' },
  music: { name: 'Musiqa', icon: '🎵', color: '#ec4899' },
  other: { name: 'Boshqa', icon: '📚', color: '#64748b' },
};

const ClubsPage = () => {
  const [clubs, setClubs] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'other',
    teacher: '',
    schedule: '',
    location: '',
    max_members: 30,
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchClubs();
    fetchTeachers();
  }, [filterCategory, currentPage]);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (filterCategory) params.category = filterCategory;
      if (searchTerm) params.search = searchTerm;
      
      const response = await clubsAPI.getAll(params);
      const data = response.data;
      if (data.results) {
        setClubs(data.results);
        setTotalCount(data.count || 0);
      } else {
        setClubs(Array.isArray(data) ? data : []);
        setTotalCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Xato:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await teachersAPI.getAll();
      setTeachers(response.data.results || response.data || []);
    } catch (error) {
      console.error('Xato:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (!data.teacher) delete data.teacher;
      
      if (editingClub) {
        await clubsAPI.update(editingClub.id, data);
      } else {
        await clubsAPI.create(data);
      }
      setShowModal(false);
      resetForm();
      fetchClubs();
    } catch (error) {
      console.error('Xato:', error);
      alert('Xatolik yuz berdi');
    }
  };

  const handleDelete = async (club) => {
    if (window.confirm(`"${club.name}" to'garagini o'chirmoqchimisiz?`)) {
      try {
        await clubsAPI.delete(club.id);
        fetchClubs();
      } catch (error) {
        alert('Xatolik yuz berdi');
      }
    }
  };

  const openEditModal = (club) => {
    setEditingClub(club);
    setFormData({
      name: club.name,
      description: club.description || '',
      category: club.category,
      teacher: club.teacher || '',
      schedule: club.schedule || '',
      location: club.location || '',
      max_members: club.max_members,
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingClub(null);
    setFormData({
      name: '',
      description: '',
      category: 'other',
      teacher: '',
      schedule: '',
      location: '',
      max_members: 30,
    });
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchClubs();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="page-container">
      <header className="page-header">
        <div className="page-title">
          <h1><Users size={28} /> To'garaklar</h1>
          <p>To'garaklarni boshqarish</p>
        </div>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <Plus size={18} />
          Yangi to'garak
        </button>
      </header>

      {/* Filterlar */}
      <div className="filters-row">
        <div className="search-bar">
          <Search size={20} />
          <input 
            type="text" 
            placeholder="To'garak qidirish..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button className="btn btn-sm btn-primary" onClick={handleSearch}>
            Qidirish
          </button>
        </div>
        <select 
          className="filter-select"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="">Barcha kategoriyalar</option>
          {Object.entries(CATEGORIES).map(([key, cat]) => (
            <option key={key} value={key}>{cat.icon} {cat.name}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <span>Jami: <strong>{totalCount}</strong> ta to'garak</span>
        {totalPages > 1 && <span>Sahifa: <strong>{currentPage}</strong> / {totalPages}</span>}
      </div>

      {/* To'garaklar grid */}
      {loading ? (
        <div className="loading-container">
          <Loader2 className="spinner" size={40} />
        </div>
      ) : clubs.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <p>To'garaklar topilmadi</p>
        </div>
      ) : (
        <>
        <div className="clubs-grid">
          {clubs.map(club => {
            const cat = CATEGORIES[club.category] || CATEGORIES.other;
            return (
              <div key={club.id} className="club-card" style={{ '--club-color': cat.color }}>
                <div className="club-header">
                  <div className="club-icon">{cat.icon}</div>
                  <div className="club-category">{cat.name}</div>
                  <div className="club-actions">
                    <button className="icon-btn" onClick={() => openEditModal(club)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="icon-btn danger" onClick={() => handleDelete(club)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <h3 className="club-name">{club.name}</h3>
                {club.description && <p className="club-desc">{club.description}</p>}
                <div className="club-info">
                  {club.teacher_name && (
                    <div className="club-info-item">
                      <User size={14} />
                      <span>{club.teacher_name}</span>
                    </div>
                  )}
                  {club.schedule && (
                    <div className="club-info-item">
                      <Clock size={14} />
                      <span>{club.schedule}</span>
                    </div>
                  )}
                  {club.location && (
                    <div className="club-info-item">
                      <MapPin size={14} />
                      <span>{club.location}</span>
                    </div>
                  )}
                </div>
                <div className="club-footer">
                  <div className="club-members">
                    <Users size={16} />
                    <span>{club.members_count || 0} / {club.max_members}</span>
                  </div>
                  {club.is_full && <span className="club-full-badge">To'lgan</span>}
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
        </>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingClub ? "To'garakni tahrirlash" : "Yangi to'garak"}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label>To'garak nomi *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Kategoriya</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                    >
                      {Object.entries(CATEGORIES).map(([key, cat]) => (
                        <option key={key} value={key}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label>Tavsif</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Rahbar o'qituvchi</label>
                    <select
                      value={formData.teacher}
                      onChange={(e) => setFormData({...formData, teacher: e.target.value})}
                    >
                      <option value="">Tanlanmagan</option>
                      {teachers.map(t => (
                        <option key={t.id} value={t.id}>{t.user?.full_name || t.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Maks. a'zolar</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_members}
                      onChange={(e) => setFormData({...formData, max_members: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Jadval</label>
                    <input
                      type="text"
                      placeholder="Masalan: Dushanba, Chorshanba 15:00"
                      value={formData.schedule}
                      onChange={(e) => setFormData({...formData, schedule: e.target.value})}
                    />
                  </div>
                  <div className="form-group">
                    <label>Joylashuv</label>
                    <input
                      type="text"
                      placeholder="Masalan: 205-xona"
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Bekor qilish
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingClub ? 'Saqlash' : 'Yaratish'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubsPage;
